/* Daily nutrition rollup, weekly adherence and the adaptive maintenance
 * (TDEE) estimate from logged intake and the weight trend. Pure functions;
 * the Python twin is src/fitlogic/energy.py and both are pinned by
 * vectors/energy.json.
 *
 * Day status: logStatus[date] wins when set; otherwise a day with entries is
 * 'partial' under 50% of the kcal target or with fewer than 3 items, else
 * 'complete', and a day without entries is 'unknown'. Averages and adherence
 * use complete days that have entries, so a forgotten dinner never drags a
 * week down. Entry totals are per x amount, like the app's entryMacros. */

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const LOG_STATUSES = ['complete', 'partial', 'untracked'];
const MIN_ITEMS = 3;
const ESTIMATE_SOURCES = ['estimate', 'photo'];
const KCAL_PER_KG = 7700;
const DEFAULT_DAYS = 28;
const MIN_COMPLETE_DAYS = 14;
const MIN_COVERAGE = 0.75;
const MIN_WEIGH_INS = 8;
const MIN_WEIGHT_SPAN = 14;
const CREATINE_LOOKBACK = 21;
const CREATINE_RE = /creatin/i;
// % of bodyweight per week the goal aims for.
const GOAL_RATES = { 'lose fat': -0.5, 'build muscle': 0.25 };

function isNum(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function num(v) {
  return isNum(v) ? v : 0;
}

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/* Half-up like the Python twin's floor(x * f + 0.5) / f; `|| 0` drops -0. */
function roundF(x, f) {
  return Math.round(x * f) / f || 0;
}
function round1(x) { return roundF(x, 10); }
function round2(x) { return roundF(x, 100); }
function roundStep(x, step) { return (Math.round(x / step) * step) || 0; }

function plural(n, word) {
  return `${n} ${n === 1 ? word : `${word}s`}`;
}

function dayNumber(key) {
  if (typeof key !== 'string' || !DATE_RE.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1, d));
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round(back.getTime() / 86400000);
}

function keyOf(n) {
  return new Date(n * 86400000).toISOString().slice(0, 10);
}

/* Monday = 0; day 0 (1970-01-01) was a Thursday. */
function monIndex(n) {
  return (((n + 3) % 7) + 7) % 7;
}

/* Raw sums of a day's entries; plain loops so the floats match the twin. */
function totals(entries) {
  const t = { kcal: 0, protein: 0, carbs: 0, fats: 0, items: 0, estimateKcal: 0 };
  for (const e of Array.isArray(entries) ? entries : []) {
    if (!isObj(e)) continue;
    const per = isObj(e.per) ? e.per : {};
    let amt = e.amount != null ? e.amount : (e.servings != null ? e.servings : 1);
    amt = isNum(amt) ? amt : 0;
    const kcal = num(per.kcal) * amt;
    t.kcal += kcal;
    t.protein += num(per.protein) * amt;
    t.carbs += num(per.carbs) * amt;
    t.fats += num(per.fats) * amt;
    t.items += 1;
    if (ESTIMATE_SOURCES.includes(e.source)) t.estimateKcal += kcal;
  }
  return t;
}

function rounded(t) {
  return { kcal: roundF(t.kcal, 1), protein: round1(t.protein), carbs: round1(t.carbs), fats: round1(t.fats), items: t.items };
}

/**
 * Totals of one day's food entries (per x amount; amount falls back to
 * servings, then 1).
 * @param {Array} entries nutrition[date]
 * @returns {{kcal:number, protein:number, carbs:number, fats:number, items:number}}
 *   kcal whole, macros 0.1 g; items = entries counted
 */
export function nutritionDay(entries) {
  return rounded(totals(entries));
}

function targetsOf(state, override) {
  if (isObj(override)) {
    return { kcal: num(override.kcal), protein: num(override.protein), carbs: num(override.carbs), fats: num(override.fats) };
  }
  const profile = isObj(state.profile) ? state.profile : {};
  const macros = isObj(profile.macros) ? profile.macros : {};
  return { kcal: num(profile.calorieTarget), protein: num(macros.protein), carbs: num(macros.carbs), fats: num(macros.fats) };
}

function entriesOn(state, date) {
  const day = isObj(state.nutrition) ? state.nutrition[date] : null;
  return Array.isArray(day) ? day : [];
}

/* Status of a day plus why it isn't complete. */
function dayCheck(state, date, tg) {
  const raw = totals(entriesOn(state, date));
  const day = rounded(raw);
  const marked = isObj(state.logStatus) && LOG_STATUSES.includes(state.logStatus[date]) ? state.logStatus[date] : null;
  let status = 'complete';
  let reason = null;
  if (marked === 'partial' || marked === 'untracked') { status = marked; reason = `marked-${marked}`; }
  else if (marked === 'complete') { status = 'complete'; }
  else if (!raw.items) { status = 'unknown'; reason = 'no-entries'; }
  else if (tg.kcal > 0 && day.kcal * 2 < tg.kcal) { status = 'partial'; reason = 'under-half-target'; }
  else if (raw.items < MIN_ITEMS) { status = 'partial'; reason = 'few-items'; }
  return { status, reason, day, raw };
}

function stateOf(state) {
  return isObj(state) ? state : {};
}

/**
 * How complete a day's food log is.
 * @param {object} state reads nutrition, logStatus, profile
 * @param {string} date YYYY-MM-DD
 * @param {{kcal?:number}} [targets] defaults to profile.calorieTarget
 * @returns {'complete'|'partial'|'untracked'|'unknown'} logStatus first; then
 *   'unknown' without entries, 'partial' under 50% of the kcal target (when
 *   there is one) or with fewer than 3 items, else 'complete'
 */
export function dayStatus(state, date, targets) {
  const st = stateOf(state);
  return dayCheck(st, date, targetsOf(st, targets)).status;
}

/* date -> weight, the last valid weigh-in of a date winning. */
function weightsByDate(state) {
  const out = new Map();
  for (const m of Array.isArray(state.bodyMetrics) ? state.bodyMetrics : []) {
    if (isObj(m) && dayNumber(m.date) !== null && num(m.weight) > 0) out.set(m.date, m.weight);
  }
  return out;
}

function meanWeight(weights, from, to) {
  let sum = 0;
  let n = 0;
  for (let d = from; d <= to; d += 1) {
    const w = weights.get(keyOf(d));
    if (w !== undefined) { sum += w; n += 1; }
  }
  return n ? sum / n : null;
}

function training(state, date) {
  const day = isObj(state.logs) ? state.logs[date] : null;
  let setsDone = 0;
  let volume = 0;
  if (isObj(day)) {
    for (const sets of Object.values(day)) {
      if (!Array.isArray(sets)) continue;
      for (const s of sets) {
        if (!isObj(s) || !s.done) continue;
        setsDone += 1;
        volume += num(s.weight) * num(s.reps);
      }
    }
  }
  return { setsDone, volume: round1(volume) };
}

function supplementNames(state, date) {
  const supp = isObj(state.supplements) ? state.supplements : {};
  const ids = isObj(supp.log) && Array.isArray(supp.log[date]) ? supp.log[date] : [];
  const items = Array.isArray(supp.items) ? supp.items : [];
  const out = [];
  for (const id of ids) {
    if (typeof id !== 'string' || !id) continue;
    const item = items.find(i => isObj(i) && i.id === id);
    // A removed item loses its name; the id still says something was taken.
    const name = item && typeof item.name === 'string' && item.name ? item.name : id;
    if (!out.includes(name)) out.push(name);
  }
  return out;
}

function range(from, to) {
  const a = dayNumber(from);
  const b = dayNumber(to);
  return a === null || b === null || a > b ? null : [a, b];
}

/**
 * One row per date from..to (inclusive) for nutrition, training and weight.
 * @param {object} state reads nutrition, logStatus, profile, bodyMetrics,
 *   logs, supplements
 * @param {string} from YYYY-MM-DD
 * @param {string} to YYYY-MM-DD
 * @param {{targets?: object}} [opts] targets override for the day status
 * @returns {Array<{date, kcal, protein, carbs, fats, items, status, weight:number|null,
 *   avg7d:number|null, trained:boolean, setsDone:number, volume:number, supplements:string[]}>}
 *   avg7d = mean weigh-in of date-6..date (0.01 kg); setsDone counts sets
 *   ticked done; volume = done weight x reps (0.1); supplements by item name
 *   (the id when the item is gone); [] for an invalid or reversed range
 */
export function dailyRollup(state, from, to, opts = {}) {
  const st = stateOf(state);
  const r = range(from, to);
  if (!r) return [];
  const tg = targetsOf(st, opts && opts.targets);
  const weights = weightsByDate(st);
  const rows = [];
  for (let d = r[0]; d <= r[1]; d += 1) {
    const date = keyOf(d);
    const chk = dayCheck(st, date, tg);
    const avg = meanWeight(weights, d - 6, d);
    const tr = training(st, date);
    rows.push({
      date,
      ...chk.day,
      status: chk.status,
      weight: weights.has(date) ? weights.get(date) : null,
      avg7d: avg === null ? null : round2(avg),
      trained: tr.setsDone > 0,
      setsDone: tr.setsDone,
      volume: tr.volume,
      supplements: supplementNames(st, date),
    });
  }
  return rows;
}

function averages(days) {
  if (!days.length) return null;
  const s = { kcal: 0, protein: 0, carbs: 0, fats: 0 };
  for (const d of days) {
    s.kcal += d.kcal;
    s.protein += d.protein;
    s.carbs += d.carbs;
    s.fats += d.fats;
  }
  const n = days.length;
  return { kcal: roundF(s.kcal / n, 1), protein: round1(s.protein / n), carbs: round1(s.carbs / n), fats: round1(s.fats / n) };
}

/**
 * Calendar weeks (Mon-Sun) touching from..to, oldest first. Nutrition counts
 * only dates inside from..to; averages and adherence use complete days with
 * entries.
 * @param {object} state reads nutrition, logStatus, profile, bodyMetrics, logs
 * @param {string} from YYYY-MM-DD
 * @param {string} to YYYY-MM-DD (pass yesterday: today is never complete)
 * @param {{targets?: object}} [opts] targets override (default profile)
 * @returns {Array<{weekStart:string, days:number, loggedDays:number,
 *   avg:{kcal, protein, carbs, fats}|null, rawAvg:{kcal, protein, carbs, fats}|null,
 *   kcalOnTargetDays:number|null, proteinHitDays:number|null, trainedDays:number,
 *   avgWeight:number|null, weightChange:number|null, proteinPerKg:number|null}>}
 *   days = dates of the week inside the range; loggedDays = complete days;
 *   rawAvg over every day with entries; on target = kcal within +-10%,
 *   protein hit = >= 90% (null without that target); avgWeight = mean
 *   weigh-in Monday..min(Sunday, to), weightChange against the week before
 *   (0.01 kg); proteinPerKg = avg protein / avgWeight
 */
export function weeklyAdherence(state, from, to, opts = {}) {
  const st = stateOf(state);
  const r = range(from, to);
  if (!r) return [];
  const tg = targetsOf(st, opts && opts.targets);
  const weights = weightsByDate(st);
  const rows = [];
  for (let ws = r[0] - monIndex(r[0]); ws <= r[1]; ws += 7) {
    const complete = [];
    const logged = [];
    let days = 0;
    let trainedDays = 0;
    for (let d = Math.max(ws, r[0]); d <= Math.min(ws + 6, r[1]); d += 1) {
      const date = keyOf(d);
      const chk = dayCheck(st, date, tg);
      days += 1;
      if (chk.day.items) logged.push(chk.day);
      if (chk.status === 'complete' && chk.day.items) complete.push(chk.day);
      if (training(st, date).setsDone > 0) trainedDays += 1;
    }
    const avg = averages(complete);
    const mean = meanWeight(weights, ws, Math.min(ws + 6, r[1]));
    const prev = meanWeight(weights, ws - 7, ws - 1);
    let protein = 0;
    for (const d of complete) protein += d.protein;
    rows.push({
      weekStart: keyOf(ws),
      days,
      loggedDays: complete.length,
      avg,
      rawAvg: averages(logged),
      kcalOnTargetDays: tg.kcal > 0 ? complete.filter(d => Math.abs(d.kcal - tg.kcal) * 10 <= tg.kcal).length : null,
      proteinHitDays: tg.protein > 0 ? complete.filter(d => d.protein * 10 >= tg.protein * 9).length : null,
      trainedDays,
      avgWeight: mean === null ? null : round2(mean),
      weightChange: mean === null || prev === null ? null : round2(mean - prev),
      proteinPerKg: avg && mean !== null ? round2(protein / complete.length / mean) : null,
    });
  }
  return rows;
}

/* Least-squares fit of weight on day; plain loops so the floats match the
 * twin (Python 3.12+ sum() compensates). */
function lsq(points) {
  const n = points.length;
  const d0 = points[0].day;
  let sx = 0;
  let sy = 0;
  for (const p of points) { sx += p.day - d0; sy += p.weight; }
  const mx = sx / n;
  const my = sy / n;
  let sxx = 0;
  let sxy = 0;
  for (const p of points) {
    const dx = p.day - d0 - mx;
    sxx += dx * dx;
    sxy += dx * (p.weight - my);
  }
  if (sxx === 0) return null;
  const b = sxy / sxx;
  let sse = 0;
  for (const p of points) {
    const r = p.weight - (my + b * (p.day - d0 - mx));
    sse += r * r;
  }
  return { b, my, sxx, sse };
}

function creatineStart(state) {
  const supp = isObj(state.supplements) ? state.supplements : {};
  const items = Array.isArray(supp.items) ? supp.items : [];
  const ids = items.filter(i => isObj(i) && typeof i.name === 'string' && CREATINE_RE.test(i.name)).map(i => i.id);
  if (!ids.length || !isObj(supp.log)) return null;
  let first = null;
  for (const date of Object.keys(supp.log)) {
    const day = dayNumber(date);
    const taken = supp.log[date];
    if (day === null || !Array.isArray(taken) || !taken.some(id => ids.includes(id))) continue;
    if (first === null || day < first) first = day;
  }
  return first;
}

/**
 * Adaptive maintenance estimate (I20). The window is the `days` days ending
 * the day BEFORE endDate (today is never complete). A day counts when its
 * status is complete, it has entries and estimate/photo entries are at most
 * 40% of its kcal. It needs max(14, 75% of days) such days and 8+ weigh-ins
 * whose first and last lie at least 14 calendar days apart inclusive.
 * TDEE = mean intake - slope (kg/day, least squares) x 7700, +- SE x 7700.
 * Suggested kcal = TDEE + goal rate (% bodyweight / week: lose fat -0.5,
 * build muscle +0.25, else 0) x mean weight x 7700 / 7, rounded to 50.
 * @param {object} state reads nutrition, logStatus, profile, bodyMetrics, supplements
 * @param {string} endDate YYYY-MM-DD, usually today (not included)
 * @param {{days?: number, targets?: object, goalRatePct?: number}} [opts]
 * @returns {null|{ok:boolean, need:string|null, from:string, to:string, days:number,
 *   completeDays:number, neededDays:number, coverage:number, avgIntake:number|null,
 *   weighIns:number, weightSpanDays:number, avgWeight:number|null,
 *   slopeKgPerWeek:number|null, seKgPerWeek:number|null, tdee:number|null,
 *   plusMinus:number|null, goal:string|null, goalRatePct:number,
 *   goalRateKgPerWeek:number|null, suggestedKcal:number|null,
 *   excluded:Array<{date:string, reason:string}>, caveats:Array<{code:string, text:string}>}}
 *   null for an invalid endDate. tdee and plusMinus round to 10 kcal and are
 *   null unless ok. Reasons: marked-partial, marked-untracked, no-entries,
 *   under-half-target, few-items, mostly-estimates. Caveat 'creatine': the
 *   first creatine tick (by item name) is within 21 days before the window
 *   or inside it, so water weight makes the estimate read low.
 */
export function estimateEnergyBalance(state, endDate, opts = {}) {
  const st = stateOf(state);
  const o = isObj(opts) ? opts : {};
  const end = dayNumber(endDate);
  if (end === null) return null;
  const days = isNum(o.days) && o.days >= 1 ? Math.floor(o.days) : DEFAULT_DAYS;
  const from = end - days;
  const to = end - 1;
  const tg = targetsOf(st, o.targets);

  const excluded = [];
  let completeDays = 0;
  let kcalSum = 0;
  for (let d = from; d <= to; d += 1) {
    const date = keyOf(d);
    const chk = dayCheck(st, date, tg);
    let reason = chk.reason;
    if (!reason && !chk.raw.items) reason = 'no-entries';
    if (!reason && chk.raw.estimateKcal * 5 > chk.raw.kcal * 2) reason = 'mostly-estimates';
    if (reason) excluded.push({ date, reason });
    else { completeDays += 1; kcalSum += chk.day.kcal; }
  }
  const neededDays = Math.max(MIN_COMPLETE_DAYS, Math.ceil(days * MIN_COVERAGE));

  const weights = weightsByDate(st);
  const points = [];
  for (let d = from; d <= to; d += 1) {
    const w = weights.get(keyOf(d));
    if (w !== undefined) points.push({ day: d, weight: w });
  }
  const n = points.length;
  const span = n ? points[n - 1].day - points[0].day + 1 : 0;
  const fit = n >= 2 ? lsq(points) : null;
  const se = fit && n > 2 ? Math.sqrt(fit.sse / (n - 2) / fit.sxx) : null;

  const parts = [];
  if (completeDays < neededDays) parts.push(plural(neededDays - completeDays, 'more logged day'));
  if (n < MIN_WEIGH_INS) parts.push(plural(MIN_WEIGH_INS - n, 'more weigh-in'));
  else if (span < MIN_WEIGHT_SPAN) parts.push(`weigh-ins across ${MIN_WEIGHT_SPAN} days (now ${span})`);
  const ok = !parts.length && fit !== null && se !== null;

  const goal = isObj(st.profile) && typeof st.profile.goal === 'string' ? st.profile.goal : null;
  const ratePct = isNum(o.goalRatePct) ? o.goalRatePct
    : (Object.prototype.hasOwnProperty.call(GOAL_RATES, goal) ? GOAL_RATES[goal] : 0);
  const rateKg = fit ? ratePct / 100 * fit.my : null;
  const avgIntake = completeDays ? kcalSum / completeDays : null;
  const tdee = ok ? avgIntake - fit.b * KCAL_PER_KG : null;

  const caveats = [];
  const creatine = creatineStart(st);
  if (creatine !== null && creatine >= from - CREATINE_LOOKBACK && creatine <= to) {
    caveats.push({ code: 'creatine', text: 'Creatine started recently: its 1-2 kg of water makes maintenance read low.' });
  }

  return {
    ok,
    need: parts.length ? `need ${parts.join(' and ')}` : null,
    from: keyOf(from),
    to: keyOf(to),
    days,
    completeDays,
    neededDays,
    coverage: round2(completeDays / days),
    avgIntake: avgIntake === null ? null : roundF(avgIntake, 1),
    weighIns: n,
    weightSpanDays: span,
    avgWeight: fit ? round2(fit.my) : (n ? round2(points[0].weight) : null),
    slopeKgPerWeek: fit ? round2(fit.b * 7) : null,
    seKgPerWeek: se === null ? null : round2(se * 7),
    tdee: tdee === null ? null : roundStep(tdee, 10),
    plusMinus: ok ? roundStep(se * KCAL_PER_KG, 10) : null,
    goal,
    goalRatePct: ratePct,
    goalRateKgPerWeek: rateKg === null ? null : round2(rateKg),
    suggestedKcal: ok ? roundStep(tdee + rateKg * KCAL_PER_KG / 7, 50) : null,
    excluded,
    caveats,
  };
}
